/**
 * This component uses Portable Text to render a post body.
 *
 * You can learn more about Portable Text on:
 * https://www.sanity.io/docs/block-content
 * https://github.com/portabletext/react-portabletext
 * https://portabletext.org/
 *
 */

// eslint-disable-next-line no-restricted-imports
import { PortableText, type PortableTextBlock, type PortableTextComponents } from 'next-sanity'

import { Accordion } from '@/app/components/Accordion'
import { Anchor } from '@/app/components/Anchor'
import { Button } from '@/app/components/Button'
import { Callout } from '@/app/components/Callout'
import { ContactForm } from '@/app/components/ContactForm'
import { ContentImage } from '@/app/components/ContentImage'
import { Group } from '@/app/components/PageBuilder/Group'
import { Spacing } from '@/app/components/PageBuilder/Spacing'
import { createPortableTextLists } from '@/app/components/portableText/lists'
import { createPortableTextMarks } from '@/app/components/portableText/marks'
import { Table } from '@/app/components/Table'
import { TableOfContents } from '@/app/components/TableOfContents'
import Video from '@/app/components/Video'
import {
  getHeadingClasses,
  getSubheadingClasses,
  type HeadingStyleConfig,
  type SubheadingStyleConfig,
} from '@/app/config/headingStyles'
import { TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { generateElementId } from '@/app/utils/text'

export default function CustomPortableText({
  className,
  value,
  tracking,
  headingConfig,
  subheadingConfig,
  buttonClassName,
  paragraphClassName,
  asFragment,
  documentId,
  documentType,
  parentPath,
  componentOverrides,
}: {
  className?: string
  value: PortableTextBlock[]
  tracking: TrackingLocation
  /** Optional partial heading size configuration. Only override specific heading levels, others use defaults. */
  headingConfig?: Partial<HeadingStyleConfig>
  /** Optional partial subheading size configuration. Only override specific subheading levels, others use defaults. */
  subheadingConfig?: Partial<SubheadingStyleConfig>
  /** Optional className to apply to all buttons rendered in the content */
  buttonClassName?: string
  /** Optional className to apply to all paragraph elements */
  paragraphClassName?: string
  /** Render without wrapper div - useful when parent needs direct children (e.g., flex containers) */
  asFragment?: boolean
  /** Document ID for presentation mode - passed down from page/post */
  documentId?: string
  /** Document type for presentation mode - passed down from page/post */
  documentType?: string
  /** Parent path for presentation mode - e.g., 'pageBuilder[_key=="xyz"].content' */
  parentPath?: string
  /** Optional component overrides to customize specific block renderers */
  componentOverrides?: Partial<PortableTextComponents>
}) {
  const baseComponents: PortableTextComponents = {
    block: {
      'normal': ({ children }) => (
        <p
          className={cn(
            'size-body-sm lg:size-body-md mb-md lg:mb-lg [&_a]:font-medium [&_a]:underline',
            paragraphClassName,
          )}
        >
          {children}
        </p>
      ),
      'small': ({ children }) => (
        <p
          className={cn(
            'size-body-xs lg:size-body-sm mb-sm lg:mb-dm [&_a]:font-medium [&_a]:underline',
            paragraphClassName,
          )}
        >
          {children}
        </p>
      ),
      'large': ({ children }) => (
        <p
          className={cn(
            'size-body-md lg:size-body-lg mb-lg lg:mb-xl [&_a]:font-medium [&_a]:underline',
            paragraphClassName,
          )}
        >
          {children}
        </p>
      ),
      'h1': ({ children }) => {
        const id = generateElementId(children)
        return (
          <h1
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn(
              'group relative',
              getHeadingClasses('h1', headingConfig),
              'mt-3xl lg:mt-5xl mb-5xl',
            )}
          >
            {children}
          </h1>
        )
      },
      'h2': ({ children }) => {
        const id = generateElementId(children)
        return (
          <h2
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn(
              'group relative',
              getHeadingClasses('h2', headingConfig),
              'mt-3xl lg:mt-5xl mb-xl lg:mb-3xl',
            )}
          >
            {children}
          </h2>
        )
      },
      'h3': ({ children }) => {
        const id = generateElementId(children)
        return (
          <h3
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn(
              'group relative',
              getHeadingClasses('h3', headingConfig),
              'mt-3xl lg:mt-5xl mb-lg lg:mb-2xl',
            )}
          >
            {children}
          </h3>
        )
      },
      'h4': ({ children }) => {
        const id = generateElementId(children)
        return (
          <h4
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn(
              'group relative',
              getHeadingClasses('h4', headingConfig),
              'mt-3xl lg:mt-5xl mb-md lg:mb-xl',
            )}
          >
            {children}
          </h4>
        )
      },
      'h5': ({ children }) => {
        const id = generateElementId(children)
        return (
          <h5
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn(
              'group relative',
              getHeadingClasses('h5', headingConfig),
              'mt-3xl lg:mt-5xl mb-md lg:mb-xl',
            )}
          >
            {children}
          </h5>
        )
      },
      'h6': ({ children }) => {
        const id = generateElementId(children)
        return (
          <h6
            id={id}
            data-heading-id={id}
            data-anchor-expose="true"
            className={cn(
              'group relative',
              getHeadingClasses('h6', headingConfig),
              'mt-3xl lg:mt-5xl mb-md lg:mb-xl',
            )}
          >
            {children}
          </h6>
        )
      },
      // Subheadings - render as <p> elements with subheading styling
      'subheading-2xl': ({ children }) => {
        const id = generateElementId(children)
        return (
          <p
            id={id}
            className={cn(
              'font-bold',
              getSubheadingClasses('subheading-2xl', subheadingConfig),
              'mt-3xl lg:mt-5xl mb-5xl',
            )}
          >
            {children}
          </p>
        )
      },
      'subheading-xl': ({ children }) => {
        const id = generateElementId(children)
        return (
          <p
            id={id}
            className={cn(
              'font-bold',
              getSubheadingClasses('subheading-xl', subheadingConfig),
              'mt-3xl lg:mt-5xl mb-xl lg:mb-3xl',
            )}
          >
            {children}
          </p>
        )
      },
      'subheading-lg': ({ children }) => {
        const id = generateElementId(children)
        return (
          <p
            id={id}
            className={cn(
              'font-bold',
              getSubheadingClasses('subheading-lg', subheadingConfig),
              'mt-3xl lg:mt-5xl mb-lg lg:mb-2xl',
            )}
          >
            {children}
          </p>
        )
      },
      'subheading-md': ({ children }) => {
        const id = generateElementId(children)
        return (
          <p
            id={id}
            className={cn(
              'font-bold',
              getSubheadingClasses('subheading-md', subheadingConfig),
              'mt-3xl lg:mt-5xl mb-md lg:mb-xl',
            )}
          >
            {children}
          </p>
        )
      },
      'subheading-sm': ({ children }) => {
        const id = generateElementId(children)
        return (
          <p
            id={id}
            className={cn(
              'font-bold',
              getSubheadingClasses('subheading-sm', subheadingConfig),
              'mt-3xl lg:mt-5xl mb-md lg:mb-xl',
            )}
          >
            {children}
          </p>
        )
      },
      'subheading-xs': ({ children }) => {
        const id = generateElementId(children)
        return (
          <p
            id={id}
            className={cn(
              'font-bold',
              getSubheadingClasses('subheading-xs', subheadingConfig),
              'mt-3xl lg:mt-5xl mb-md lg:mb-xl',
            )}
          >
            {children}
          </p>
        )
      },
      // Overlines
      'overline': ({ children }) => (
        <p className="font-black uppercase size-overline-xs lg:size-overline-md mt-5xl mb-xl">
          {children}
        </p>
      ),
      'overline-primary': ({ children }) => (
        <p className="font-black uppercase size-overline-md lg:size-overline-lg mt-5xl mb-xl text-primary-700">
          {children}
        </p>
      ),
      'blockquote': ({ children }) => (
        <blockquote className="size-quote-sm lg:size-quote-md py-2xl md:px-5xl md:pt-5xl lg:py-xl">
          {children}
        </blockquote>
      ),
      'quote-lg': ({ children }) => (
        <blockquote className="size-quote-md lg:size-quote-lg py-2xl md:px-5xl md:pt-5xl lg:py-xl">
          {children}
        </blockquote>
      ),
      'quote-author': ({ children }) => (
        <p className="font-black uppercase size-overline-xs text-primary-700 lg:size-overline-md pb-2xl md:px-5xl md:pb-5xl lg:pt-0 lg:pb-2xl">
          {children}
        </p>
      ),
    },
    types: {
      accordion: ({ value }) => (
        <Accordion
          value={value}
          tracking={tracking}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      anchor: ({ value }) => <Anchor anchorId={value.anchorId} />,
      button: ({ value }) => {
        return <Button button={value} tracking={tracking} className={buttonClassName} />
      },
      callout: ({ value: callout }) => (
        <Callout
          callout={callout}
          tracking={tracking}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      contactForm: ({ value: contactForm }) => (
        <ContactForm
          contactForm={contactForm}
          tracking={tracking}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      contentImage: ({ value }) => (
        <ContentImage
          value={value}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      contentTable: ({ value }) => <Table value={value} />,
      group: ({ value }) => (
        <Group
          value={value}
          documentId={documentId}
          documentType={documentType}
          parentPath={parentPath}
        />
      ),
      tableOfContents: () => <TableOfContents allBlocks={value} />,
      separator: () => <hr className="my-3xl lg:my-5xl" />,
      spacing: Spacing,
      video: ({ value }) => (
        <Video
          video={value}
          className="my-3xl lg:my-5xl overflow-hidden rounded-sm lg:rounded-md"
        />
      ),
    },
    marks: createPortableTextMarks(tracking),
    ...createPortableTextLists(),
  }

  // Merge component overrides (deep merge for block types)
  let components: PortableTextComponents = baseComponents

  if (componentOverrides) {
    components = {
      ...baseComponents,
      ...componentOverrides,
    }

    // Deep merge block components if both exist
    if (baseComponents.block && componentOverrides.block) {
      if (
        typeof baseComponents.block === 'object' &&
        typeof componentOverrides.block === 'object'
      ) {
        components.block = {
          ...baseComponents.block,
          ...componentOverrides.block,
        }
      }
    }
  }

  if (asFragment) {
    return <PortableText components={components} value={value} />
  }

  return (
    <div
      className={cn(
        'text-neutral-900 [&_a]:text-brand [&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className,
      )}
    >
      <PortableText components={components} value={value} />
    </div>
  )
}
